import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { ApiClient } from '../../api/client';
import type { ComplimentaryCareEnrollment, Enrollee } from '../../api/types';
import { DocumentPreviewModal, type DocumentPreviewTarget } from '../../components/DocumentPreviewModal';
import { EnrollmentStatusBadge } from '../../components/EnrollmentStatusBadge';
import {
  describeExistingCare,
  relationshipLabel,
  submissionKindLabel,
} from '../../components/enrollmentLabels';
import { useHubContext } from '../../shell/HubContext';
import { HrNoteCard } from './HrNoteCard';
import { ReviewActionButtons, ReviewConfirmModal } from './ReviewActions';
import { useReviewActions } from './useReviewActions';

/**
 * One enrollment, on its own page and its own URL — HR reads the whole form and the documents behind
 * it before deciding, which is more than a dialog over a table should hold. The documents themselves
 * do open in a dialog, since that is a glance rather than a place to be.
 */
export function EnrollmentDetailPage() {
  const { api } = useHubContext();
  const { enrollmentId } = useParams();
  const navigate = useNavigate();

  const [enrollment, setEnrollment] = useState<ComplimentaryCareEnrollment | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<DocumentPreviewTarget | null>(null);

  const actions = useReviewActions(api, async () => {
    if (enrollmentId) {
      setEnrollment(await api.getComplimentaryCareEnrollment(enrollmentId));
    }
  });

  useEffect(() => {
    if (!enrollmentId) return;

    const controller = new AbortController();

    (async () => {
      try {
        setEnrollment(await api.getComplimentaryCareEnrollment(enrollmentId, controller.signal));
      } catch (err) {
        if (!controller.signal.aborted) {
          setLoadError(err instanceof Error ? err.message : 'Could not load the enrollment.');
        }
      }
    })();

    return () => controller.abort();
  }, [api, enrollmentId]);

  if (loadError) {
    return (
      <div className="page-message page-message--error">
        <h2>Could not load the enrollment</h2>
        <p>{loadError}</p>
        <p>
          <Link to="/complimentary-care-review">Back to all enrollments</Link>
        </p>
      </div>
    );
  }

  if (!enrollment) {
    return <div className="page-message">Loading…</div>;
  }

  return (
    <div className="care-page care-page--wide">
      <Link className="care-back" to="/complimentary-care-review">
        ← All enrollments
      </Link>

      <header className="care-detail__header">
        <div>
          <h1 className="care-page__title">{enrollment.employeeDisplayName}</h1>
          <p className="care-page__subtitle">{enrollment.employeeUpn}</p>
          {/* Same tag as the row this page was opened from — what approving would actually do is not
              something to learn from the confirmation dialog. */}
          {submissionKindLabel(enrollment) && (
            <div
              className={
                enrollment.kind === 'reEnrollment' ? 'care-tag care-tag--attention' : 'care-tag'
              }
            >
              {submissionKindLabel(enrollment)}
            </div>
          )}
        </div>
        <EnrollmentStatusBadge status={enrollment.status} />
      </header>

      {actions.error && (
        <div className="issues__block issues__block--error">
          <p>{actions.error}</p>
        </div>
      )}

      {enrollment.changes.length > 0 && (
        <div className="care-changes">
          <h2 className="care-changes__title">
            What changed since the last approval
            {enrollment.resubmittedAtUtc && (
              <span className="care-changes__when">
                {' '}
                · edited {new Date(enrollment.resubmittedAtUtc).toLocaleString()}
              </span>
            )}
          </h2>
          <ul>
            {enrollment.changes.map((change) => (
              <li key={change}>{change}</li>
            ))}
          </ul>
        </div>
      )}

      <section className="group">
        <h2 className="group__legend">Enrollment</h2>

        {/* No "Employee" fact here — the page header above is already the employee's name with the
            UPN under it, and a third copy only crowded the row. */}
        <dl className="care-detail__facts">
          <div>
            <dt>Hire date</dt>
            <dd>{enrollment.hireDate}</dd>
          </div>
          <div>
            <dt>Submitted</dt>
            <dd>{new Date(enrollment.submittedAtUtc).toLocaleString()}</dd>
          </div>
          <div>
            <dt>Last edited</dt>
            <dd>
              {enrollment.resubmittedAtUtc ? new Date(enrollment.resubmittedAtUtc).toLocaleString() : 'Never'}
            </dd>
          </div>
          <div>
            <dt>Already receiving care</dt>
            <dd>{describeExistingCare(enrollment)}</dd>
          </div>
          <div>
            <dt>Chiro One Wellness Center</dt>
            <dd>
              {enrollment.enrollees.find((e) => e.relationship === 'teamMember')?.wellnessCenter ?? '—'}
            </dd>
          </div>
          <div>
            <dt>Enrollees</dt>
            <dd>{enrollment.enrollees.length}</dd>
          </div>
        </dl>

        {(enrollment.reviewedByUpn || enrollment.deactivatedByUpn) && (
          <div className="care-detail__review">
            {enrollment.reviewedByUpn && (
              <p>
                Reviewed by <strong>{enrollment.reviewedByUpn}</strong>
                {enrollment.reviewedAtUtc && ` on ${new Date(enrollment.reviewedAtUtc).toLocaleString()}`}
              </p>
            )}
            {enrollment.deactivatedByUpn && (
              <p>
                Deactivated by <strong>{enrollment.deactivatedByUpn}</strong>
                {enrollment.deactivatedAtUtc && ` on ${new Date(enrollment.deactivatedAtUtc).toLocaleString()}`}
              </p>
            )}
            {enrollment.reviewNote && <p className="care-detail__note">“{enrollment.reviewNote}”</p>}
          </div>
        )}
      </section>

      <section className="group">
        <h2 className="group__legend">New enrollees</h2>

        <div className="care-table__scroll">
          <table className="care-table">
            <thead>
              <tr>
                <th>Relationship</th>
                <th>Name</th>
                <th>Age</th>
                <th>Contact phone</th>
                <th>Supporting document</th>
              </tr>
            </thead>
            <tbody>
              {enrollment.enrollees.map((enrollee) => (
                <tr key={enrollee.id}>
                  <td>{relationshipLabel(enrollee.relationship)}</td>
                  <td className="care-table__primary">{enrollee.name}</td>
                  <td>{enrollee.age ?? '—'}</td>
                  <td>{enrollee.contactPhoneNumber ?? '—'}</td>
                  <td>
                    {enrollee.documentFileName ? (
                      <button
                        type="button"
                        className="care-document-link"
                        onClick={() => setPreviewing(previewTarget(enrollment, enrollee, api))}
                      >
                        {enrollee.documentFileName}
                      </button>
                    ) : (
                      <span className="care-table__muted">Not required</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Last, under everything the form itself says: the notes are HR's own, not part of what the
          employee submitted. */}
      <HrNoteCard enrollment={enrollment} onSaved={setEnrollment} />

      <div className="care-actionbar">
        <button type="button" className="button button--quiet" onClick={() => navigate('/complimentary-care-review')}>
          Back
        </button>
        <div className="care-actionbar__decisions">
          <ReviewActionButtons
            enrollment={enrollment}
            onAction={actions.request}
            busy={actions.busyId !== null}
          />
        </div>
      </div>

      {previewing && <DocumentPreviewModal target={previewing} onClose={() => setPreviewing(null)} />}

      <ReviewConfirmModal
        confirming={actions.confirming}
        note={actions.note}
        noteError={actions.noteError}
        busy={actions.busyId !== null}
        onNoteChange={actions.setNote}
        onConfirm={actions.confirm}
        onCancel={actions.cancel}
      />
    </div>
  );
}

function previewTarget(
  enrollment: ComplimentaryCareEnrollment,
  enrollee: Enrollee,
  api: ApiClient,
): DocumentPreviewTarget {
  const fileName = enrollee.documentFileName ?? 'document';

  return {
    title: `${enrollee.name} — ${fileName}`,
    fileName,
    load: (signal) => api.downloadEnrolleeDocument(enrollment.id, enrollee.id, signal),
  };
}
